import type { ReactNode } from 'react'
import React, { useCallback, useEffect, useRef, useState } from 'react'

import { Dropdown, Icon, InputUI } from '@fourthwall/components'
import type { IconComponent } from '@fourthwall/components/src/common/Icon/types'
import { ArrowShortDownIcon, ArrowShortUpIcon } from '@fourthwall/icons'

import * as S from './AnalyticsSelectTool.styled'

type AnalyticsSelectToolProps = {
  label: string
  disabled?: boolean
  icon?: IconComponent
  iconOpen?: IconComponent
  renderContent: ({ close }: { close: () => void }) => ReactNode
}

export const AnalyticsSelectTool = ({
  label,
  disabled = false,
  icon,
  iconOpen,
  renderContent,
}: AnalyticsSelectToolProps) => {
  const [isOpen, setOpen] = useState(false)
  const [containerWidth, setContainerWidth] = useState<number>(0)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth)
      }
    }

    updateWidth()
    window.addEventListener('resize', updateWidth)

    return () => {
      window.removeEventListener('resize', updateWidth)
    }
  }, [label])

  const getIcon = useCallback(() => {
    if (!icon) {
      return isOpen ? ArrowShortUpIcon : ArrowShortDownIcon
    }

    if (isOpen && iconOpen) {
      return iconOpen
    }

    return icon
  }, [icon, iconOpen, isOpen])

  return (
    <Dropdown
      isOpen={isOpen}
      content={
        <S.Content minWidth={containerWidth}>
          {renderContent({ close: () => setOpen(false) })}
        </S.Content>
      }
      position="bottom-start"
      maxWidth={900}
      onClose={() => setOpen(false)}
    >
      <InputUI.Root>
        <InputUI.Box $disabled={disabled} $size="small" $focused={isOpen}>
          <S.Container
            ref={containerRef}
            tabIndex={0}
            $disabled={disabled}
            onClick={() => setOpen((status) => !status)}
          >
            <S.Label>{label}</S.Label>
            <S.Icon>
              <Icon component={getIcon()} height={16} />
            </S.Icon>
          </S.Container>
        </InputUI.Box>
      </InputUI.Root>
    </Dropdown>
  )
}
